import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, addDays, parseISO } from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw, AlertTriangle, Search, Users } from 'lucide-react';
import axios from '../lib/axios';
import Layout from '../components/Layout';

// Dubai "today" (the app operates in Asia/Dubai, UTC+4)
const dubaiToday = () => new Date(Date.now() + 4 * 3600e3).toISOString().slice(0, 10);

// Category -> colour. Matched by keyword so new category names still land sensibly.
/**
 * Four colours for the AMC visit and its three stages, one grey for everything
 * else. The board exists to answer "where does an AMC booking fit?", and seven
 * colours spent most of the palette on work that is only ever context —
 * breakdowns, PDIs and workshop jobs all read the same way here: time this
 * technician cannot give to an AMC collection.
 *
 * Order matters: "AMC - Workshop" must match AMC before the workshop rule, so
 * the AMC test comes first.
 */
function catStyle(category = '') {
  const c = category.toLowerCase();
  if (c.includes('amc')) return { bg: 'bg-brand-primary', text: 'text-white', dot: '#1c6635' };
  if (c.includes('collection')) return { bg: 'bg-pink-500', text: 'text-white', dot: '#ec4899' };
  if (c.includes('clean') || c.includes('wash')) return { bg: 'bg-sky-500', text: 'text-white', dot: '#0ea5e9' };
  if (c.includes('delivery')) return { bg: 'bg-violet-500', text: 'text-white', dot: '#8b5cf6' };
  return { bg: 'bg-slate-400', text: 'text-white', dot: '#94a3b8' };
}

const LEGEND = [
  ['AMC', '#1c6635'], ['Collection', '#ec4899'], ['Cleaning/Wash', '#0ea5e9'],
  ['Delivery', '#8b5cf6'], ['Other work', '#94a3b8'],
];

// Greedy lane packing so overlapping jobs stack instead of hiding each other.
function packLanes(jobs) {
  const sorted = [...jobs].filter((j) => j.startMin != null && j.endMin != null).sort((a, b) => a.startMin - b.startMin);
  const laneEnds = [];
  for (const j of sorted) {
    let placed = false;
    for (let i = 0; i < laneEnds.length; i++) {
      if (laneEnds[i] <= j.startMin) { j._lane = i; laneEnds[i] = j.endMin; placed = true; break; }
    }
    if (!placed) { j._lane = laneEnds.length; laneEnds.push(j.endMin); }
  }
  return { jobs: sorted, lanes: Math.max(1, laneEnds.length) };
}

export default function TechnicianCalendar() {
  const [date, setDate] = useState(dubaiToday());
  // AMC + collection/washing/delivery technicians only. Everyone else in Zuper
  // is somebody else's schedule and only makes this board harder to read.
  const [scope, setScope] = useState('amc');
  const [filter, setFilter] = useState('');
  const [view, setView] = useState('board'); // 'board' (dispatch timeline) | 'agenda' (calendar list)

  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['tech-calendar', date, scope],
    queryFn: async () => {
      const { data } = await axios.get('/api/calendar', { params: { from: date, to: date, scope } });
      return data;
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const technicians = useMemo(() => {
    const list = data?.technicians || [];
    if (!filter.trim()) return list;
    const q = filter.trim().toLowerCase();
    return list.filter((t) => t.user_name.toLowerCase().includes(q));
  }, [data, filter]);

  // Time window (Dubai minutes) — clamp to the day's jobs so nothing is clipped.
  const window = useMemo(() => {
    let min = 6 * 60, max = 19 * 60;
    for (const t of data?.technicians || []) {
      for (const j of t.jobs) {
        if (j.startMin != null) min = Math.min(min, j.startMin);
        if (j.endMin != null) max = Math.max(max, j.endMin);
      }
    }
    min = Math.floor(min / 30) * 30;
    max = Math.ceil(max / 30) * 30;
    return { start: min, end: Math.max(max, min + 120) };
  }, [data]);

  const span = window.end - window.start;
  // Half-hour ticks: AMC child windows are 30 minutes (collection 08:30-09:00,
  // delivery 11:00-11:30), so an hourly ruler put both edges of a job between
  // the same pair of lines and you could not read its times off the board.
  const ticks = [];
  for (let m = window.start; m <= window.end; m += 30) ticks.push(m);
  const hours = ticks.filter((m) => m % 60 === 0);
  const pct = (min) => `${((min - window.start) / span) * 100}%`;
  const tickLabel = (min) => {
    const h = Math.floor(min / 60);
    const ap = h >= 12 ? 'PM' : 'AM';
    const hh = (h % 12) || 12;
    return min % 60 === 0 ? `${hh}${ap}` : `${hh}:30`;
  };

  // Agenda: all of the day's jobs, chronological, with exact times.
  const agenda = useMemo(() => {
    const rows = [];
    for (const t of technicians) for (const j of t.jobs) rows.push({ ...j, techName: t.user_name });
    return rows.sort((a, b) => (a.start || '').localeCompare(b.start || '') || (a.techName || '').localeCompare(b.techName || ''));
  }, [technicians]);

  const totalJobs = data?.totalJobs || 0;
  const totalOverlaps = (data?.technicians || []).reduce((n, t) => n + (t.overlapCount || 0), 0);
  const LANE_H = 30, LANE_GAP = 3, ROW_PAD = 8;

  return (
    <Layout>
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex flex-wrap justify-between items-center gap-3 z-10 shadow-sm">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <CalendarDays className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Technician Calendar</h1>
            <p className="text-xs text-brand-neutral hidden md:block">
              Live schedules from the Zuper job store
              {data?.scope === 'amc' && data?.hiddenTechnicians > 0 && (
                <span> · {data.hiddenTechnicians} other technician{data.hiddenTechnicians === 1 ? '' : 's'} hidden</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-brand-light/50 rounded-xl p-0.5">
            {[['amc', 'Our techs'], ['all', 'Everyone']].map(([v, label]) => (
              <button
                key={v}
                onClick={() => setScope(v)}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  scope === v ? 'bg-white text-brand-dark shadow-sm' : 'text-brand-neutral hover:text-brand-dark'
                }`}
                title={v === 'amc'
                  ? 'AMC mechanics and the collection, washing and delivery teams'
                  : 'Every technician with a job in Zuper'}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center bg-brand-light/50 rounded-xl p-0.5">
            <button onClick={() => setView('board')} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${view === 'board' ? 'bg-white text-brand-primary shadow-sm' : 'text-brand-neutral'}`}>Dispatch Board</button>
            <button onClick={() => setView('agenda')} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${view === 'agenda' ? 'bg-white text-brand-primary shadow-sm' : 'text-brand-neutral'}`}>Agenda</button>
          </div>
          <div className="flex items-center bg-brand-light/50 rounded-xl p-0.5">
            <button onClick={() => setDate(format(addDays(parseISO(date), -1), 'yyyy-MM-dd'))} className="p-1.5 hover:bg-white rounded-lg" title="Previous day">
              <ChevronLeft className="w-4 h-4 text-brand-dark" />
            </button>
            <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)}
              className="bg-transparent text-sm font-medium text-brand-dark px-1 focus:outline-none" />
            <button onClick={() => setDate(format(addDays(parseISO(date), 1), 'yyyy-MM-dd'))} className="p-1.5 hover:bg-white rounded-lg" title="Next day">
              <ChevronRight className="w-4 h-4 text-brand-dark" />
            </button>
          </div>
          <button onClick={() => setDate(dubaiToday())} className="px-3 py-2 text-xs font-medium text-brand-primary bg-brand-light/60 rounded-xl hover:bg-brand-light">Today</button>
          {dataUpdatedAt > 0 && <span className="hidden lg:inline text-[11px] text-brand-neutral">{isFetching ? 'Loading…' : `Updated ${new Date(dataUpdatedAt).toLocaleTimeString()}`}</span>}
          <button onClick={() => refetch()} disabled={isFetching} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-secondary disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-3 md:p-5 bg-[#f5f8f6]">
        {/* summary + filter + legend */}
        <div className="max-w-[1400px] mx-auto space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-white rounded-xl border border-brand-light/60 px-3 py-2">
              <Users className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-semibold text-brand-dark">{technicians.length}</span>
              <span className="text-xs text-brand-neutral">techs</span>
              <span className="mx-1 text-brand-light">·</span>
              <span className="text-sm font-semibold text-brand-dark">{totalJobs}</span>
              <span className="text-xs text-brand-neutral">jobs</span>
              {totalOverlaps > 0 && (
                <span className="ml-1 flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-lg">
                  <AlertTriangle className="w-3 h-3" /> {totalOverlaps} overlap{totalOverlaps === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-neutral" />
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter technician…"
                className="pl-9 pr-3 py-2 text-sm bg-white border border-brand-light/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 w-56" />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 ml-auto">
              {LEGEND.map(([label, color]) => (
                <span key={label} className="flex items-center gap-1.5 text-[11px] text-brand-neutral">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />{label}
                </span>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="bg-white rounded-2xl border border-brand-light/60 p-16 text-center">
              <RefreshCw className="w-6 h-6 animate-spin text-brand-primary mx-auto mb-2" />
              <p className="text-sm text-brand-neutral">Loading schedules…</p>
            </div>
          ) : technicians.length === 0 ? (
            <div className="bg-white rounded-2xl border border-brand-light/60 p-16 text-center">
              <CalendarDays className="w-8 h-8 text-brand-light mx-auto mb-2" />
              <p className="text-sm text-brand-neutral">No scheduled jobs for {format(parseISO(date), 'EEEE, MMM d')}.</p>
            </div>
          ) : view === 'agenda' ? (
            <div className="bg-white rounded-2xl border border-brand-light/60 overflow-hidden">
              <div className="px-4 py-2 border-b border-brand-light/60 bg-brand-light/20 text-[11px] font-semibold uppercase tracking-wider text-brand-neutral">
                {format(parseISO(date), 'EEEE, MMMM d')} · {agenda.length} jobs
              </div>
              {agenda.map((j) => {
                const s = catStyle(j.category);
                return (
                  <div key={j.job_uid + j.techName} className={`flex items-center gap-3 px-4 py-2.5 border-b border-brand-light/40 hover:bg-brand-light/10 ${j.conflict ? 'bg-red-50/40' : ''}`}>
                    <div className="w-24 shrink-0 text-sm font-semibold text-brand-dark tabular-nums">{j.startLabel}<span className="text-brand-neutral font-normal">–{j.endLabel}</span></div>
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.dot }} title={j.category} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-brand-dark truncate">{j.title || j.category}</div>
                      <div className="text-[11px] text-brand-neutral truncate">
                        {[j.customer, j.category, j.status, j.wo ? `WO ${j.wo}` : null, j.location].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="w-40 shrink-0 text-right">
                      <span className="text-xs font-medium text-brand-dark">{j.techName}</span>
                      {j.conflict && <span className="ml-1 text-[10px] font-semibold text-red-600">⚠ clash</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-brand-light/60 overflow-hidden">
              <div className="overflow-x-auto">
                <div className="min-w-[760px]">
                  {/* hour ruler */}
                  <div className="flex sticky top-0 bg-white z-10 border-b border-brand-light/60">
                    <div className="w-40 shrink-0 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-brand-neutral">Technician</div>
                    <div className="relative flex-1 h-8">
                      {ticks.map((m) => (
                        <div key={m} className="absolute top-0 bottom-0 flex items-center" style={{ left: pct(m) }}>
                          <span
                            className={`-translate-x-1/2 whitespace-nowrap ${
                              m % 60 === 0 ? 'text-[10px] text-brand-neutral' : 'text-[9px] text-brand-neutral/55'
                            }`}
                          >
                            {tickLabel(m)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* technician rows */}
                  {technicians.map((t) => {
                    const { jobs, lanes } = packLanes(t.jobs);
                    const rowH = lanes * LANE_H + (lanes - 1) * LANE_GAP + ROW_PAD * 2;
                    return (
                      <div key={t.user_uid} className="flex border-b border-brand-light/40 hover:bg-brand-light/10">
                        <div className="w-40 shrink-0 px-3 py-2 flex flex-col justify-center">
                          <span className="text-sm font-medium text-brand-dark leading-tight">{t.user_name}</span>
                          <span className="text-[11px] text-brand-neutral">
                            {t.jobs.length} job{t.jobs.length === 1 ? '' : 's'}
                            {t.overlapCount > 0 && <span className="text-red-600 font-medium"> · {t.overlapCount} clash</span>}
                          </span>
                        </div>
                        <div className="relative flex-1" style={{ height: rowH }}>
                          {/* hour gridlines */}
                          {ticks.map((m) => (
                            <div
                              key={m}
                              className={`absolute top-0 bottom-0 w-px ${m % 60 === 0 ? 'bg-brand-light/40' : 'bg-brand-light/20'}`}
                              style={{ left: pct(m) }}
                            />
                          ))}
                          {jobs.map((j) => {
                            const s = catStyle(j.category);
                            const top = ROW_PAD + j._lane * (LANE_H + LANE_GAP);
                            const faded = j.statusType && !/SCHEDULED|STARTED|IN.?PROGRESS/i.test(j.statusType) && /TBC|CANCEL|COMPLETE|CLOSED|DELIVERED/i.test(j.status || j.statusType || '');
                            return (
                              <div key={j.job_uid}
                                title={`${j.startLabel}-${j.endLabel}  ${j.title || ''}\n${j.category || ''} · ${j.status || ''}\n${j.customer || ''}${j.location ? ' · ' + j.location : ''}${j.wo ? ' · WO ' + j.wo : ''}`}
                                className={`absolute rounded-md px-1.5 overflow-hidden ${s.bg} ${s.text} ${j.conflict ? 'ring-2 ring-red-600 ring-offset-1' : ''} ${faded ? 'opacity-50' : ''}`}
                                style={{ left: pct(j.startMin), width: `calc(${pct(j.endMin)} - ${pct(j.startMin)})`, minWidth: 40, top, height: LANE_H }}>
                                <div className="flex items-center gap-1 h-full">
                                  <span className="text-[10px] font-semibold whitespace-nowrap opacity-90">{j.startLabel}</span>
                                  <span className="text-[11px] font-medium truncate">{j.title || j.category}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </Layout>
  );
}
